import { z } from 'zod'
import { accountBrandSchema } from './account'
import { coverageRuleInputSchema, coverageRuleSchema } from './coverage'
import { MANUAL_PRELOAD_CHOICES } from './delivery-schedule'
import { sheetLayoutSchema } from './sheet-layout'
import {
  isHtmlTemplateDocument,
  proseTargets,
  sectionPresentationSchema,
  type TemplateDocument,
} from './template'
import { insightsModeSchema } from './insights'
import { articleSortSchema } from './template-article'
import { hexColorSchema } from './template-primitives'

/**
 * Report configuration — the "what data exists and when it goes out" half of the ownership
 * rule (2026-08-24 sign-off). Presentation (snippets, TOC, highlighting, sort-for-display)
 * lives in the report template; brand lives on the account. Nothing here may grow a field
 * that decides how something LOOKS.
 */

/** "HH:MM", 24h. Kept as a string — timezone context lives beside it, arithmetic doesn't. */
const sendTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM')

export const reportScheduleSchema = z.enum(['daily', 'weekdays', 'weekly', 'custom'])

/**
 * The relevance-judge model menu — legacy's exactly (the webapp's Derek page offers the same two):
 * `''` is the data API's default (Haiku 4.5 today, its `NEWS_RELEVANCE_MODEL`), the other an
 * OpenRouter slug the judge understands. Not an enum on the settings field (see `derekModel`): a
 * stored value that has since dropped off the menu must keep parsing, and is sent as the default.
 */
export const DEREK_MODELS = [
  { value: '', label: 'Haiku (default)' },
  { value: 'anthropic/claude-sonnet-5', label: 'Sonnet 5 (medium reasoning)' },
] as const

/**
 * The judge-model override a report's `derekModel` amounts to: the slug when it is on the menu,
 * nothing for the default and for a value no longer on the menu — the pre-parity `gpt-5-mini`
 * default, say, which the data API's judge would reject and fail open on: no verdicts, no
 * filtering, nothing in a log.
 */
export function derekModelOverride(derekModel: string): string | undefined {
  const known = DEREK_MODELS.some((model) => model.value === derekModel)
  return known && derekModel !== '' ? derekModel : undefined
}

/**
 * The report's settings document — jsonb on the row, this schema at every edge. One editor
 * (the report config form), no cross-report queries; the scheduler promotion seam is
 * documented on the table. `version` is the shape-migration handle.
 */
export const sendModeSchema = z.enum(['auto', 'manual'])
export type SendMode = z.infer<typeof sendModeSchema>

/*
 * Snippet settings used to live here as well as on the template, and the report-level copy was
 * inert: the renderer reads `presentation.snippets` from the template and never looked at these
 * (2026-08-25 tech sync). Configuring the same thing in two places, where only one of them did
 * anything, is worse than configuring it twice — so this half is gone. Stored settings keep
 * parsing: zod strips the leftover key.
 */

/**
 * The report's own words around the coverage, each following the template until set (CLI-793).
 *
 * Content, not presentation, which is why it is allowed here: the legacy portal kept the greeting,
 * the message and the sign-off switch on the subscription and rendered every one of them through
 * a shared template, and a client's own greeting is no more a template decision than its
 * recipients are. How the words LOOK — size, typeface — stays the template's (`shell.prose`).
 *
 * Each override replaces the CONTENT of the matching block in whatever template the report uses
 * (`applyReportProse` in template.ts), so one template serves every client and a template without
 * that block simply has nothing to override.
 */
export const reportProseSchema = z.object({
  /** Rich-text HTML for the template's greeting block, or null to keep the template's. */
  greeting: z.string().max(5000).nullable().default(null),
  /** Rich-text HTML for the template's intro (its first text block above the headers), or null. */
  intro: z.string().max(5000).nullable().default(null),
  /** `hidden` drops the template's sign-off for this report — the legacy `doNotIncludeSignature`. */
  signOff: z.enum(['template', 'hidden']).default('template'),
})
export type ReportProse = z.infer<typeof reportProseSchema>

/**
 * The document a report actually renders: its template with this report's words in place.
 *
 * An override replaces the block's content and turns the block on — a report that wrote its own
 * greeting wants it sent even under a template that ships the greeting switched off — and drops
 * the block's `htmlOverride`, which would otherwise win over the content and swallow the override
 * silently. The template row itself is never touched; this runs per render.
 */
export function applyReportProse(doc: TemplateDocument, prose: ReportProse): TemplateDocument {
  // An HTML document takes the report's words as slot values; the renderer puts each into its
  // `data-cb-slot` element (and drops the sign-off slot when hidden). A template without a slot
  // simply has nothing to override — the same rule as a block document without that block.
  if (isHtmlTemplateDocument(doc)) {
    return {
      ...doc,
      slots: {
        ...doc.slots,
        ...(prose.greeting !== null && { greeting: prose.greeting }),
        ...(prose.intro !== null && { intro: prose.intro }),
        ...(prose.signOff === 'hidden' && { hideSignOff: true }),
      },
    }
  }
  const targets = proseTargets(doc)
  const blocks = doc.blocks.map((block) => {
    if (block.type === 'greeting' && block.id === targets.greeting && prose.greeting !== null) {
      const { htmlOverride: _dropped, ...rest } = block
      return { ...rest, enabled: true, content: prose.greeting }
    }
    if (block.type === 'richText' && block.id === targets.intro && prose.intro !== null) {
      const { htmlOverride: _dropped, ...rest } = block
      return { ...rest, enabled: true, content: prose.intro }
    }
    if (block.type === 'signature' && block.id === targets.signOff && prose.signOff === 'hidden') {
      return { ...block, enabled: false }
    }
    return block
  })
  return { ...doc, blocks }
}

/** Markup the rich-text editor writes — not any `<word>`, which plain text can contain. */
const HTML_TAG = /<\/?(p|div|br|span|a|b|i|u|em|strong|ul|ol|li|h[1-6])(\s[^>]*)?\/?>/i

/** Plain text typed into a textarea (the old "Email body", the global notice), as paragraphs. */
export function plainTextAsHtml(text: string): string {
  const escape = (line: string) =>
    line.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => `<p>${paragraph.split('\n').map(escape).join('<br>')}</p>`)
    .join('')
}

/**
 * `delivery.emailBody` was a field nothing rendered — the Delivery tab offered it as "the message
 * shown above the coverage" and the legacy migration filled it, but no renderer ever read it. It is
 * the intro override now, so a stored report that set one gets it as `prose.intro` the first time
 * it is parsed, and the old key is dropped like any other leftover.
 */
function liftEmailBody(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw
  const settings = raw as { delivery?: { emailBody?: unknown }; prose?: { intro?: unknown } }
  const body = settings.delivery?.emailBody
  if (typeof body !== 'string' || body.trim() === '' || settings.prose?.intro !== undefined) {
    return raw
  }
  const intro = HTML_TAG.test(body) ? body.trim() : plainTextAsHtml(body.trim())
  return { ...settings, prose: { ...settings.prose, intro } }
}

export const reportSettingsSchema = z.preprocess(
  liftEmailBody,
  z.object({
    version: z.literal(1),
    delivery: z.object({
      /** auto = assembles and sends on schedule; manual = an analyst builds and sends. */
      sendMode: sendModeSchema.default('manual'),
      /**
       * How long before each send moment a manual report's draft is pulled (CLI-1005), one of
       * MANUAL_PRELOAD_CHOICES; null takes the default. Read only through `preloadMinutesOf`, and
       * only for a manual report: an automatic report's build lead is the system's.
       */
      preloadMinutes: z.literal(MANUAL_PRELOAD_CHOICES).nullable().default(null),
      schedule: reportScheduleSchema,
      /** IANA zone, e.g. "America/New_York". */
      timezone: z.string().min(1).max(64),
      sendTimes: z.array(sendTimeSchema).min(1).max(6),
      /** Weekly/custom schedules name their days; 0 = Sunday. Empty for daily/weekdays. */
      sendDays: z.array(z.number().int().min(0).max(6)).default([]),
      recipients: z.object({
        to: z.array(z.email()).default([]),
        cc: z.array(z.email()).default([]),
        bcc: z.array(z.email()).default([]),
      }),
      /**
       * This report is still delivered by the legacy portal, so Headline must not send it.
       *
       * Both systems hold the same recipients while the migration runs, and the portal keeps sending
       * exactly as it did. A report migrated from a legacy subscription is therefore legacy-delivered
       * until somebody decides otherwise, per report, and Headline refuses every send for it — the
       * button, the test and the scheduled callback alike. A report created in Headline is not.
       */
      legacyDelivery: z.boolean().default(false),
      senderName: z.string().max(120).default(''),
      senderEmail: z.string().max(255).default(''),
      subject: z.string().max(200).default(''),
      multiSend: z.object({
        enabled: z.boolean().default(false),
        additionalTimes: z.array(sendTimeSchema).default([]),
        sameDayDedup: z.boolean().default(true),
      }),
      exports: z.object({
        csv: z.boolean().default(false),
        googleDoc: z.boolean().default(false),
        googleSheet: z.boolean().default(false),
      }),
      /**
       * The Google Doc template — a template whose target is a Google Doc, its tabs each naming an
       * email template (template-layout.ts `docTabs`). Null is a one-tab document in the report's
       * own email template, which is what most reports want and what the admin portal made
       * (CLI-925). Only read when `exports.googleDoc` is on.
       */
      googleDocTemplateId: z.uuid().nullable().default(null),
      /** How a Google Sheet is laid out (CLI-942, sheet-layout.ts). Only read when `exports.googleSheet` is on. */
      sheetLayout: sheetLayoutSchema.default({ preset: 'portal' }),
    }),
    curation: z.object({
      /**
       * Cross-header dedup is always on: a story an earlier header carries is skipped by later
       * headers, except a header marked `protected` (see sectionSettingsSchema), which keeps every
       * story it matched. A report that wants no dedup protects every header — one switch, one
       * place. The report-level `crossSectionDedup` flag (and the older `protectedTopicIds`) is
       * stripped on parse (Kevin, 2026-09-18).
       */
      /**
       * Whether a story's "Syndicated By" line prints at all — in the email and the Google Doc.
       * Off is the admin portal's `removeSyndication` (11 live reports, CLI-976); the copies stay
       * on the story for the editor, only the line goes.
       */
      includeSyndication: z.boolean().default(true),
      syndicationLimit: z.number().int().min(1).max(50).default(3),
      /**
       * Leaves the hosted "Read more" link out of this report's emails. The link itself follows
       * the account's `source:full-text` entitlement — its LexisNexis licence, the same switch the
       * pull sends to the data API — as legacy's pill followed `lexis_nexis_full_text_enabled`.
       * This is legacy's `remove_ln_link`, the opt-out seven subscriptions carried (2026-10-07).
       * There is no opt-in: an older `fullText` key is stripped on parse.
       */
      hideFullTextLinks: z.boolean().default(false),
      /**
       * The order every header's stories start in, over the template's — null follows the
       * template. A header with a format of its own keeps its own. `term` is the admin portal's
       * `sort_articles_by_alphabetical_order` (NewDEAL, CLI-1010): stories grouped by the name
       * each matched, A–Z — a choice about one client's report, not about a layout other
       * accounts share, so it lives here rather than in a template copy per client.
       */
      articleSort: articleSortSchema.nullable().default(null),
    }),
    /**
     * Whose brand THIS report wears, or null to follow the account's.
     *
     * Set beside the template picker, because "which template, and in what colour" is one decision
     * and asking it in two places is how the account settings page ended up carrying a control that
     * had nothing to do with accounts.
     *
     * Deliberately not on the template: templates are shareable across accounts, so a template
     * carrying a colour would push one client's brand into every account that borrowed it — the bug
     * the per-header move already had to undo once.
     */
    brand: accountBrandSchema.nullable().default(null),
    insights: z.object({
      /** Generation only. Placement and formatting are the template's. */
      auto: z.boolean().default(false),
      coverage: z.boolean().default(true),
      /** Which insights an issue starts with — `INSIGHTS_MODES` (CLI-959). */
      mode: insightsModeSchema.default('off'),
      /**
       * Whether the Google Doc carries the list too. Legacy's `includeInsightsInGoogleDoc`: off, the
       * list rides in the email's letter and the document leaves it out, as legacy's did.
       */
      inGoogleDoc: z.boolean().default(false),
      /**
       * Every header in the `by-header` list, a header with nothing to list as its name alone —
       * legacy's `showEmptyHeadersInTOC`.
       */
      showEmptyHeaders: z.boolean().default(false),
    }),
    /**
     * Report-wide preamble for the Derek relevance judge, prepended to every header's own
     * prompt — client context every header shares ("the client is …, ignore stock tickers").
     * Null = no preamble.
     */
    derekPreamble: z.string().max(4000).nullable().default(null),
    /**
     * Which model runs the relevance judge for this report: `''` = the data API's default, else an
     * OpenRouter slug from `DEREK_MODELS`. A plain string, not an enum: the model catalogue is
     * operational config that moves faster than a schema release, and an unknown value degrades to
     * the default (`derekModelOverride`) rather than failing a report's whole settings parse.
     */
    derekModel: z.string().max(128).default(''),
    /**
     * Whether Derek actually removes articles it judges irrelevant. Off = it still scores them (so
     * the agreement surface has numbers) but drops nothing — the safe default when a report's
     * prompts are still being tuned. On by default; a report with no prompts keeps everything anyway.
     */
    derekFiltering: z.boolean().default(true),
    /**
     * The legacy admin-portal subscription this report still runs on, or null. Set, the pull asks
     * clipbook-data-api for each mapped header's content by header id instead of matching this
     * report's own topics against the warehouse — the same queries, limits and relevance judge the
     * portal applies, for as long as both systems produce the same report.
     */
    legacySubscriptionId: z.string().trim().min(1).max(64).nullable().default(null),
    prose: reportProseSchema.default({ greeting: null, intro: null, signOff: 'template' }),
  }),
)
export type ReportSettings = z.infer<typeof reportSettingsSchema>

export function defaultReportSettings(): ReportSettings {
  return reportSettingsSchema.parse({
    version: 1,
    delivery: {
      schedule: 'weekdays',
      timezone: 'America/New_York',
      sendTimes: ['06:00'],
      recipients: {},
      multiSend: {},
      exports: {},
    },
    curation: {},
    insights: {},
  })
}

/**
 * A header's settings document — data limits and ranking sources only. Sort, translate,
 * opinion/paywall markers, snippets and colors are all per-header PRESENTATION and live in
 * the template's per-header map (keyed on this header's id); the priority order below is the
 * DATA the template's "outlet priority" sort consumes.
 */
export const sectionSettingsSchema = z.object({
  version: z.literal(1),
  // No header limit: the topic limits are the limits (Kevin, 2026-09-15). A stored `headerLimit`
  // from before is stripped on parse and ignored.
  /** A second line under the header name — "Regulatory Coverage" under a federal header. Empty = none. */
  subtitle: z.string().trim().max(200).default(''),
  /**
   * The heading printed in the email when it differs from the section's unique name. Legacy let a
   * subscription repeat a header name (State + Local Election Alliance has "Nevada" six times);
   * a section's name is unique per report, so the migration numbers the repeats ("Nevada (2)")
   * and keeps the legacy heading here — the same for a legacy name that held a non-breaking space.
   * The email, its table of contents and the doc print this (`sectionHeading` in
   * @headline/report-render); the editor keeps the unique name. Absent = the name is the heading.
   */
  displayName: z.string().trim().min(1).max(120).optional(),
  /**
   * Keep every story this header matched, even one an earlier header already carries. Legacy kept
   * principal matches in every header that matched them (`remove_duplicates_for_principal` off,
   * the default); this is that, per header. Only meaningful while the report's cross-header dedup
   * is on. Within a header a story never repeats either way.
   */
  protected: z.boolean().default(false),
  /**
   * Mark this header's opinion pieces "[Opinion] - " in the email (`printsOpinion`, CLI-974). The
   * admin portal's per-header `isHeaderOpinion`, off by default as there: without it a story the
   * data calls opinion prints plain unless an associate flags it.
   */
  markOpinion: z.boolean().default(false),
  /**
   * The legacy admin-portal header this header was migrated from, written by the migration
   * generator (scripts/legacy-migration) and by nothing else. It changes nothing about the
   * pull: it is the header id a send records in the legacy delivery ledger
   * (`report_hits.header_id`), which the migrated section's own id cannot give back — that id is a
   * one-way uuid5 over this value. Absent on a header created in Headline, which the ledger then
   * skips. Declared here so a save from the editor (which sends the whole settings document back)
   * keeps it: zod strips a key the schema does not name.
   */
  migratedFromHeaderId: z.string().trim().min(1).max(64).optional(),
  /** Ordered ad-hoc outlet ranking, used when no priority list is attached. */
  adHocPriority: z.array(z.string().min(1).max(255)).default([]),
  /**
   * This header's Derek relevance prompt — "An article is relevant if…", a `DEDUPLICATION:` block
   * allowed at the end as on the legacy page. The report's derekPreamble is prepended when judging.
   * Null = no prompt, Derek keeps everything. 8,000: the longest legacy criteria box is 4,313.
   */
  derekPrompt: z.string().max(8000).nullable().default(null),
})
export type SectionSettings = z.infer<typeof sectionSettingsSchema>

export function defaultSectionSettings(): SectionSettings {
  return sectionSettingsSchema.parse({ version: 1 })
}

/**
 * What a topic may contribute to a header, per source, when nobody has set a number: 100 for
 * everything (Kevin, 2026-09-15). Generous enough that a principal topic on an ordinary day is
 * never cut, small enough to stop a runaway one; the migration writes explicit numbers where the
 * history says otherwise. There is no header limit above this — the topic limits are the limits.
 */
export const DEFAULT_TOPIC_LIMIT = 100
export const MAX_TOPIC_LIMIT = 500
/** The data API's ceiling on a topic's per-query cap (`queryLimit`). */
export const MAX_QUERY_LIMIT = 5000

/** A topic attached to a header, with the sources that feed it (empty = all enabled). */
export const sectionTopicSchema = z.object({
  topicId: z.uuid(),
  dataSources: z.array(z.string().min(1).max(40)).default([]),
  position: z.number().int().min(0).default(0),
  /**
   * Max articles this topic contributes to the header, applied PER SOURCE: 20 on a topic carrying
   * news and podcast means up to 20 of each, not 20 between them. Null = DEFAULT_TOPIC_LIMIT.
   */
  topicLimit: z.number().int().min(1).max(MAX_TOPIC_LIMIT).nullable().default(null),
  /** Per-source overrides of `topicLimit`, keyed by source — `{ x: 20 }`. Absent = use topicLimit. */
  sourceLimits: z.record(z.string().min(1).max(40), z.number().int().min(1).max(500)).default({}),
  /**
   * Legacy's per-query cap inside the topic, for news and LinkedIn: no single query contributes
   * more than this many stories to the header (the portal's 100 per principal / 20 per secondary
   * query, or its `header_article_limit`). Null = no per-query cap, `topicLimit` alone.
   */
  queryLimit: z.number().int().min(1).max(MAX_QUERY_LIMIT).nullable().default(null),
  /**
   * The colour this topic's matched terms are highlighted in, in this header (CLI-964). Null =
   * the template's term colour. `CLIPBOOK_HIGHLIGHT_YELLOW` is the canned choice — legacy's yellow
   * for a principal's or organization's name; any other `#rrggbb` is a custom pick.
   */
  highlightColor: hexColorSchema.nullable().default(null),
})
export type SectionTopic = z.infer<typeof sectionTopicSchema>

export const reportSectionSchema = z.object({
  id: z.uuid(),
  reportId: z.uuid(),
  name: z.string().min(1).max(120),
  settings: sectionSettingsSchema,
  /**
   * How THIS header's articles look, overriding the template's house style. Null means "render
   * like every other header", which is what almost every header wants.
   *
   * Presentation is the template's business in general, and this is the one report-specific part
   * of it: "Social shows engagement counts, everything else doesn't" is a fact about this report's
   * headers, not about a template that other accounts also use. Complete rather than a sparse
   * delta, so a header is either inheriting or has its own settings — never a merge whose result
   * nothing displays.
   */
  presentation: sectionPresentationSchema.nullable(),
  priorityListId: z.uuid().nullable(),
  topics: z.array(sectionTopicSchema),
  coverage: z.array(coverageRuleSchema),
  /** The header's place in the report, 0-based; `sections` arrives already in this order. */
  position: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  /** The last save of the row — name, settings (a Derek prompt, say) or presentation. */
  updatedAt: z.iso.datetime(),
})
export type ReportSection = z.infer<typeof reportSectionSchema>

export const reportStatusSchema = z.enum(['active', 'paused'])

/** An associate a report is assigned to. Name may be absent — a user row exists from the first
 *  sign-in, and the provider may not have told us a name yet; the email is the fallback label. */
export const reportOwnerSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  email: z.string().nullable(),
})
export type ReportOwner = z.infer<typeof reportOwnerSchema>

/** Setting a report's owners replaces the whole list — the picker sends its whole selection. */
export const setReportOwnersSchema = z.object({
  /** At most two: a primary and a second pair of eyes. More is a rota, not an assignment. */
  userIds: z.array(z.uuid()).max(2),
})
export type SetReportOwners = z.infer<typeof setReportOwnersSchema>

export const assignableUsersResponseSchema = z.object({
  users: z.array(reportOwnerSchema),
})
export type AssignableUsersResponse = z.infer<typeof assignableUsersResponseSchema>

export const reportSchema = z.object({
  id: z.uuid(),
  subaccountId: z.uuid(),
  name: z.string().min(1).max(200),
  status: reportStatusSchema,
  templateId: z.uuid().nullable(),
  settings: reportSettingsSchema,
  coverage: z.array(coverageRuleSchema),
  sections: z.array(reportSectionSchema),
  /** The associates this report is assigned to. Their own rows, not part of `settings`. */
  owners: z.array(reportOwnerSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type Report = z.infer<typeof reportSchema>

/** List rows omit the assembled children — the list needs names and status, not documents. */
export const reportSummarySchema = reportSchema
  .omit({ settings: true, coverage: true, sections: true, owners: true })
  .extend({ sectionCount: z.number().int().nonnegative() })
export type ReportSummary = z.infer<typeof reportSummarySchema>

export const listReportsQueryStringSchema = z.object({
  subaccountId: z.uuid(),
})

export const listReportsResponseSchema = z.object({
  reports: z.array(reportSummarySchema),
})
export type ListReportsResponse = z.infer<typeof listReportsResponseSchema>

/**
 * Whether a report's exports can be delivered together, and if not, why — the one rule the API's
 * validation and the Delivery tab's switches share (CLI-940). A report delivers ONE document: the
 * email becomes its letter and a link, so a Doc and a Sheet would mean two letters for one send.
 * The admin portal let a Doc silently win over a Sheet when both were set; here the combination is
 * refused where it is made.
 */
export function deliveryExportsConflict(
  exports: Pick<ReportSettings['delivery']['exports'], 'googleDoc' | 'googleSheet'>,
): string | null {
  return exports.googleDoc && exports.googleSheet
    ? 'A report is delivered as a Google Doc or as a Google Sheet, not both. Turn one off first.'
    : null
}

/** A settings document whose exports conflict is refused at the edge, naming the field. */
const refuseConflictingExports = (
  settings: ReportSettings | undefined,
  ctx: z.RefinementCtx,
): void => {
  const conflict = settings ? deliveryExportsConflict(settings.delivery.exports) : null
  if (conflict) {
    ctx.addIssue({
      code: 'custom',
      message: conflict,
      path: ['settings', 'delivery', 'exports'],
    })
  }
}

export const createReportSchema = z
  .object({
    subaccountId: z.uuid(),
    name: z.string().min(1).max(200),
    /** Omitted = the standard defaults; the server persists a complete document either way. */
    settings: reportSettingsSchema.optional(),
  })
  .superRefine((body, ctx) => refuseConflictingExports(body.settings, ctx))
export type CreateReport = z.infer<typeof createReportSchema>

/**
 * What deleting a report would destroy, so the confirmation can say so.
 *
 * Deleting is permanent and reaches history: a sent report is the record of something a client
 * received. A confirmation that says "this cannot be undone" without saying what "this" contains
 * is not informed consent, so the counts come from the server rather than being guessed at.
 */
export const reportDeletionScopeSchema = z.object({
  drafts: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  scheduled: z.number().int().nonnegative(),
})
export type ReportDeletionScopeResponse = z.infer<typeof reportDeletionScopeSchema>

export const updateReportSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    status: reportStatusSchema.optional(),
    templateId: z.uuid().nullable().optional(),
    settings: reportSettingsSchema.optional(),
    /** Full replacement of the report-level coverage rules. */
    coverage: z.array(coverageRuleInputSchema).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })
  .superRefine((body, ctx) => refuseConflictingExports(body.settings, ctx))
export type UpdateReport = z.infer<typeof updateReportSchema>

export const createSectionSchema = z.object({
  name: z.string().min(1).max(120),
  settings: sectionSettingsSchema.optional(),
})
export type CreateSection = z.infer<typeof createSectionSchema>

export const updateSectionSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    settings: sectionSettingsSchema.optional(),
    priorityListId: z.uuid().nullable().optional(),
    /** Null clears the override and returns the header to the template's default. */
    presentation: sectionPresentationSchema.nullable().optional(),
    topics: z.array(sectionTopicSchema).optional(),
    /** Full replacement of the section-level coverage rules. */
    coverage: z.array(coverageRuleInputSchema).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })
export type UpdateSection = z.infer<typeof updateSectionSchema>

/**
 * One row of the org-wide Reports overview (the sidebar's Reports page): every report the
 * session's organization can see, with the columns the table shows. Assembled server-side
 * so the client never fans out per sub-account.
 */
export const reportOverviewRowSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: reportStatusSchema,
  templateId: z.uuid().nullable(),
  accountId: z.uuid(),
  accountName: z.string(),
  subaccountId: z.uuid(),
  subaccountName: z.string(),
  schedule: reportScheduleSchema,
  sendTimes: z.array(z.string()),
  timezone: z.string(),
  /** Whether the report assembles and sends itself, or waits for an associate to build it.
   *  On the row because "what still needs a person today" is the first question the reports
   *  table is asked, and it was previously only visible inside a report's delivery settings. */
  sendMode: sendModeSchema,
  /** The associates this report is assigned to, in the order they were picked. */
  owners: z.array(reportOwnerSchema),
  /**
   * Where this report sits in today's delivery queue, and the issue behind it.
   *
   * `bucket` is null when the report isn't scheduled for today at all — a weekday report on a
   * Saturday. `issueId` is null when nobody has started today's draft, which is the common case
   * for a due report and exactly the work the queue exists to surface: due comes from the
   * SCHEDULE, not from a draft existing.
   */
  today: z.object({
    bucket: z.enum(['due', 'scheduled', 'completed', 'failed']).nullable(),
    issueId: z.uuid().nullable(),
    issueDate: z.string(),
    scheduledFor: z.iso.datetime().nullable(),
    sentAt: z.iso.datetime().nullable(),
    /** Set for the `failed` bucket: why, and what the provider last said — see report-issue.ts. */
    failureReason: z.string().nullable(),
    lastError: z.string().nullable(),
    /** 'manual' | 'auto' — who authored today's issue; null when there is none yet. */
    origin: z.string().nullable(),
    /** An automatic issue's moment, and how far its build has got (CLI-741). */
    targetSendAt: z.iso.datetime().nullable(),
    generateAttempts: z.number().int().nonnegative().nullable(),
    pulledAt: z.iso.datetime().nullable(),
  }),
  autoInsights: z.boolean(),
  sectionCount: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime(),
})
export type ReportOverviewRow = z.infer<typeof reportOverviewRowSchema>

export const reportsOverviewResponseSchema = z.object({
  reports: z.array(reportOverviewRowSchema),
})
export type ReportsOverviewResponse = z.infer<typeof reportsOverviewResponseSchema>
